/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {toAbsoluteUrl} from '../../../_metronic/helpers'
import type {GpsStatus} from '../VehicleMonitoring/VehicleList'

export type AvailableVehicleListItem = {
  itemId: number
  vehicleNumber: string
  vehicleType?: string | null
  baseLocation?: string | null
  speed: number | null
  gpsStatus: GpsStatus
}

type Props = {
  vehicles: AvailableVehicleListItem[]
  mapViewVehicle: number[]
  selectedFocusId: number
  selectedMarkerId: number
  searchQuery: string
  isLoading?: boolean
  onSearchChange: (value: string) => void
  onToggleVehicle: (itemId: number) => void
  onToggleAll: () => void
  onSelectVehicle: (itemId: number) => void
  onToggleFocus: (itemId: number) => void
  showingOnMapCount: number
  totalCount: number
}

const statusLabel = (status: GpsStatus) => {
  if (status === 'moving') return 'Moving'
  if (status === 'stopped') return 'Stopped'
  return 'No GPS'
}

const statusClass = (status: GpsStatus) => {
  if (status === 'moving') return 'vm-status-chip vm-status-chip--moving'
  if (status === 'stopped') return 'vm-status-chip vm-status-chip--stopped'
  return 'vm-status-chip vm-status-chip--no-gps'
}

const renderTooltip = (props: any) => <Tooltip {...props}>Focus vehicle on map</Tooltip>

export const AvailableVehicleList: React.FC<Props> = ({
  vehicles,
  mapViewVehicle,
  selectedFocusId,
  selectedMarkerId,
  searchQuery,
  isLoading,
  onSearchChange,
  onToggleVehicle,
  onToggleAll,
  onSelectVehicle,
  onToggleFocus,
  showingOnMapCount,
  totalCount,
}) => {
  const allFilteredSelected =
    vehicles.length > 0 && vehicles.every((v) => mapViewVehicle.includes(v.itemId))

  return (
    <div className='vm-vehicle-list h-100 d-flex flex-column'>
      <div className='d-flex align-items-center px-3 bg-gray-800 sticky-header'>
        <div className='d-flex align-items-center justify-content-between w-100 py-3 flex-wrap gap-2'>
          <div>
            <h3 className='fs-7 text-white mb-0'>Vehicle List</h3>
            <span className='text-gray-400 fs-9'>
              Showing {showingOnMapCount} of {totalCount} on map
            </span>
          </div>
          <div className='d-flex align-items-center position-relative flex-nowrap'>
            <input
              id='av-search'
              type='text'
              className='form-control form-control-white form-control-sm max-w-150px custom-search-input-height'
              placeholder='Plate'
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className='vm-vehicle-list__body flex-grow-1 position-relative'>
        {isLoading && (
          <div className='vm-list-loading-overlay'>
            <div className='spinner-border spinner-border-sm text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        )}

        {totalCount === 0 && !isLoading ? (
          <div className='d-flex text-center justify-content-center flex-column align-items-center h-100'>
            <img
              alt='Empty'
              src={toAbsoluteUrl('/media/other/empty.png')}
              className='h-auto w-50px pt-8'
              style={{opacity: '0.5'}}
            />
            <h3 className='text-gray-400 fs-7 mt-3 fw-normal pb-8'>No Records Found</h3>
          </div>
        ) : (
          <ul className='list-unstyled mb-0'>
            <li className='d-flex align-items-center py-2 px-1 vm-vehicle-row'>
              <div className='d-flex align-items-center justify-content-between w-100'>
                <div className='d-flex align-items-center'>
                  <div className='form-check form-check-custom form-check-solid form-check-sm'>
                    <input
                      className='form-check-input mx-2'
                      type='checkbox'
                      checked={allFilteredSelected}
                      onChange={onToggleAll}
                      id='av-select-all'
                      disabled={vehicles.length === 0}
                    />
                  </div>
                  <span className='text-dark fw-bold fs-7'>
                    All{vehicles.length ? ` (${vehicles.length})` : ''}
                  </span>
                </div>
              </div>
            </li>

            {vehicles.length === 0 && searchQuery ? (
              <li className='px-4 py-6 text-center text-gray-500 fs-7'>No matching vehicles</li>
            ) : null}

            {totalCount > 0 && showingOnMapCount === 0 && !searchQuery ? (
              <li className='px-3 py-2'>
                <div className='vm-empty-map-hint'>Select vehicles from the list to show on map</div>
              </li>
            ) : null}

            {vehicles.map((vehicle) => {
              const isSelected =
                selectedMarkerId === vehicle.itemId || selectedFocusId === vehicle.itemId
              const secondaryParts = [vehicle.vehicleType, vehicle.baseLocation].filter(Boolean)

              return (
                <li
                  key={vehicle.itemId}
                  className={`d-flex align-items-center py-2 px-1 vm-vehicle-row ${
                    isSelected ? 'vm-vehicle-row--selected' : ''
                  }`}
                >
                  <div className='d-flex align-items-center justify-content-between w-100'>
                    <div className='d-flex align-items-start flex-grow-1 min-w-0'>
                      <div className='form-check form-check-custom form-check-solid form-check-sm mt-1'>
                        <input
                          className='form-check-input mx-2'
                          type='checkbox'
                          checked={mapViewVehicle.includes(vehicle.itemId)}
                          onChange={() => onToggleVehicle(vehicle.itemId)}
                          onClick={(e) => e.stopPropagation()}
                          id={`av-vehicle-${vehicle.itemId}`}
                        />
                      </div>
                      <button
                        type='button'
                        className='btn btn-flush text-start p-0 flex-grow-1 min-w-0'
                        onClick={() => onSelectVehicle(vehicle.itemId)}
                      >
                        <div className='d-flex align-items-center gap-2 flex-wrap'>
                          <span className='text-dark fw-bold fs-7 text-truncate'>
                            {vehicle.vehicleNumber}
                          </span>
                          <span className={statusClass(vehicle.gpsStatus)}>
                            {statusLabel(vehicle.gpsStatus)}
                          </span>
                        </div>
                        <div className='text-gray-600 fs-8 mt-1 text-truncate'>
                          {secondaryParts.length ? secondaryParts.join(' · ') : '—'}
                          <span className='mx-1'>·</span>
                          {vehicle.speed != null ? `${vehicle.speed} km/h` : '—'}
                        </div>
                      </button>
                    </div>
                    <div className='d-flex align-items-center ms-1'>
                      <OverlayTrigger
                        placement='top'
                        delay={{show: 250, hide: 400}}
                        overlay={renderTooltip}
                      >
                        <button
                          type='button'
                          className='btn btn-flush p-0'
                          onClick={(e) => {
                            e.stopPropagation()
                            onToggleFocus(vehicle.itemId)
                          }}
                        >
                          <img
                            src={
                              selectedFocusId === vehicle.itemId
                                ? 'media/other/active-aim.svg'
                                : 'media/other/default-aim.svg'
                            }
                            alt='Focus'
                            className='svg-icon-dark w-20px h-20px'
                          />
                        </button>
                      </OverlayTrigger>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
